import { FormEvent, useState } from "react";
import { routes } from "@/router";
import { CompanyEntryDto } from "@advanced-sales-technologies/backend-client/models/CompanyEntryDto";
import { ErrorResponseDto } from "@advanced-sales-technologies/backend-client/models/ErrorResponseDto";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { HiOutlineArrowPath, HiOutlineArrowSmallRight, HiOutlineCheckCircle } from "react-icons/hi2";
import { generatePath, useNavigate } from "react-router-dom";

import { useCreateDynamicList } from "@/hooks/api/list/useCreateDynamicList";
import { SearchParams } from "@/hooks/api/search/useInfiniteSearch";
import { useToast } from "@/hooks/useToast";
import Button from "@/components/atoms/Button";
import FeaturedIcon from "@/components/atoms/FeaturedIcon";
import InputError from "@/components/atoms/input/InputError";
import BasicModalContent from "@/components/atoms/modal-panels/BasicModalContent";
import Spinner from "@/components/atoms/Spinner";
import FormInput from "@/components/molecules/form/FormInput";

type Props = {
  onClose: (value: boolean) => void;
  onSuccess: () => void;
  preventClose?: boolean;
  filter: SearchParams;
  changedCompanies: CompanyEntryDto[];
};

type FormValues = {
  listName: string;
};

const CreateDynamicListModal = ({ onClose, preventClose = false, onSuccess, filter, changedCompanies }: Props) => {
  const { t } = useTranslation();
  const [isDuplicateConfig, setIsDuplicateConfig] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { showToast } = useToast();
  const { createDynamicList } = useCreateDynamicList();
  const navigate = useNavigate();
  const methods = useForm<FormValues>({ defaultValues: { listName: "" } });
  const { setError, handleSubmit, formState } = methods;

  // We need to handle the nested form submit separately to prevent the parent form from submitting!
  const handleNestedFormSubmit = (e: FormEvent<HTMLFormElement>) => {
    // Stop the event from propagating to the parent form
    e.stopPropagation();
    handleSubmit(onSubmit)(e);
  };

  const onSubmit = (data: FormValues) => {
    setIsLoading(true);
    createDynamicList(
      { name: data.listName, force: isDuplicateConfig, ...filter, companies: changedCompanies },
      {
        onSuccess: (data) => {
          onSuccess();
          setIsLoading(false);
          navigate(
            generatePath(routes.listsDetail, {
              id: data.id,
            }),
          );
          showToast({
            featuredIcon: { icon: HiOutlineCheckCircle, color: "success" },
            heading: t("dynamic.modalCreate.addedSuccessfully"),
            children: (
              <Button
                label={t("static.viewLists")}
                onClick={() =>
                  navigate(
                    generatePath(routes.listsDetail, {
                      id: data.id,
                    }),
                  )
                }
                color={"brand"}
                trailingIcon={<HiOutlineArrowSmallRight className={"size-5"} />}
                variants={"text"}
                size={"xs"}
              />
            ),
          });
        },
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        onError: ({ body }: ErrorResponseDto) => {
          setIsLoading(false);
          if (body?.status_code === 409) {
            if (body?.internal_status_code === 40902) {
              setError("listName", {
                type: "manual",
                message: t("dynamic.modalCreate.duplicateError", { name: data.listName }),
              });
            }

            if (body?.internal_status_code === 40901) {
              setIsDuplicateConfig(true);
            }
          }
        },
      },
    );
  };

  return (
    <BasicModalContent
      featuredIcon={<FeaturedIcon icon={HiOutlineArrowPath} size={"lg"} color={"secondary"} />}
      heading={t("dynamic.modalCreate.heading")}
      preventClose={preventClose}
      body={t("dynamic.modalCreate.description")}
      onClose={onClose}
      data-testid="create-list-modal"
    >
      <FormProvider {...methods}>
        <form onSubmit={handleNestedFormSubmit}>
          <FormInput
            name={"listName"}
            label={t("dynamic.modalCreate.name")}
            placeholder={t("dynamic.modalCreate.placeholder")}
            rules={{ required: t("validation.required"), setValueAs: (value: string) => value.trim() }}
          />
          {isDuplicateConfig && !formState.errors.listName && (
            <InputError error={t("dynamic.modalCreate.duplicateConfigError")} className={"mt-1.5"} />
          )}
          <div className="mt-4 grid grid-cols-2 gap-3">
            <Button
              type={"button"}
              label={t("cancel")}
              onClick={() => onClose(false)}
              color="natural"
              variants="outline"
            />
            <Button
              type="submit"
              label={
                <>
                  {isLoading && <Spinner size={"2xs"} />} {t("dynamic.modalCreate.create")}
                </>
              }
              color="brand"
              variants="primary"
              disabled={formState.isSubmitting || isLoading}
            />
          </div>
        </form>
      </FormProvider>
    </BasicModalContent>
  );
};

export default CreateDynamicListModal;
